import React, { useState, useEffect, useCallback } from "react";
import API from "../../utils/api";

const STATUS_COLORS = {
  pending:   { bg: "#FEF3C7", color: "#92400E" },
  approved:  { bg: "#D1FAE5", color: "#065F46" },
  rejected:  { bg: "#FEE2E2", color: "#991B1B" },
  settled:   { bg: "#DBEAFE", color: "#1D4ED8" },
  cancelled: { bg: "#F3F4F6", color: "#6B7280" },
};

const STATUS_LABELS = {
  pending:   "Pending",
  approved:  "Approved",
  rejected:  "Rejected",
  settled:   "Settled",
  cancelled: "Cancelled",
};

const ALL_STATUSES = ["all", "pending", "approved", "settled", "rejected", "cancelled"];

export default function ManagerTrips() {
  const [trips, setTrips]         = useState([]);
  const [filtered, setFiltered]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [statusFilter, setStatus] = useState("all");
  const [search, setSearch]       = useState("");

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.get("/trips/team");
      setTrips(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load team trips");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTrips(); }, [fetchTrips]);

  useEffect(() => {
    let list = trips;
    if (statusFilter !== "all") list = list.filter(t => t.status === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(t =>
        t.employee_name?.toLowerCase().includes(q) ||
        t.title?.toLowerCase().includes(q) ||
        t.destination?.toLowerCase().includes(q)
      );
    }
    setFiltered(list);
  }, [trips, statusFilter, search]);

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const fmtAmt = (n) => n != null ? `₹${Number(n).toLocaleString("en-IN")}` : "—";

  // Summary counts
  const counts = {};
  for (const s of ALL_STATUSES.slice(1)) counts[s] = trips.filter(t => t.status === s).length;

  return (
    <div style={{ padding: "24px", fontFamily: "Inter, sans-serif", maxWidth: 1200 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1e293b" }}>Team Trips</h2>
        <button
          onClick={fetchTrips}
          style={{ padding: "7px 16px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}
        >
          ↺ Refresh
        </button>
      </div>

      {/* Summary pills */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
        {ALL_STATUSES.slice(1).map(s => {
          const col = STATUS_COLORS[s] || { bg: "#F3F4F6", color: "#374151" };
          return (
            <button
              key={s}
              onClick={() => setStatus(statusFilter === s ? "all" : s)}
              style={{
                padding: "5px 14px",
                borderRadius: 20,
                border: `2px solid ${statusFilter === s ? col.color : "transparent"}`,
                background: col.bg,
                color: col.color,
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              {STATUS_LABELS[s]} ({counts[s] || 0})
            </button>
          );
        })}
        <span style={{ marginLeft: "auto", fontSize: 13, color: "#64748b", alignSelf: "center" }}>
          Total: {trips.length}
        </span>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search by employee, title, or destination…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{
          width: "100%", maxWidth: 420, padding: "8px 14px",
          border: "1.5px solid #cbd5e1", borderRadius: 8,
          fontSize: 14, marginBottom: 18, boxSizing: "border-box",
        }}
      />

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#64748b" }}>Loading team trips…</div>
      ) : error ? (
        <div style={{ textAlign: "center", padding: 40, color: "#dc2626" }}>{error}</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>
          No trips found{statusFilter !== "all" ? ` with status "${STATUS_LABELS[statusFilter]}"` : ""}.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                {["Employee", "Trip Title", "Destination", "Dates", "Purpose", "Advance (₹)", "Status"].map(h => (
                  <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, color: "#475569", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, i) => {
                const sc = STATUS_COLORS[t.status] || { bg: "#F3F4F6", color: "#374151" };
                return (
                  <tr key={t.id} style={{ borderBottom: "1px solid #f1f5f9", background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                    <td style={{ padding: "10px 14px", fontWeight: 600, color: "#1e293b" }}>
                      {t.employee_name || "—"}
                      {t.department && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{t.department}</div>}
                    </td>
                    <td style={{ padding: "10px 14px", color: "#334155" }}>{t.title || "—"}</td>
                    <td style={{ padding: "10px 14px", color: "#334155" }}>{t.destination || "—"}</td>
                    <td style={{ padding: "10px 14px", color: "#334155", whiteSpace: "nowrap" }}>
                      {fmt(t.start_date)}
                      {t.end_date && t.end_date !== t.start_date && <> – {fmt(t.end_date)}</>}
                    </td>
                    <td style={{ padding: "10px 14px", color: "#64748b", maxWidth: 160 }}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {t.purpose || "—"}
                      </div>
                    </td>
                    <td style={{ padding: "10px 14px", color: "#334155", whiteSpace: "nowrap" }}>
                      {fmtAmt(t.advance_amount)}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{
                        padding: "3px 10px",
                        borderRadius: 12,
                        background: sc.bg,
                        color: sc.color,
                        fontWeight: 600,
                        fontSize: 12,
                        whiteSpace: "nowrap",
                      }}>
                        {STATUS_LABELS[t.status] || t.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
